import { useState, useEffect, type ReactNode } from "react";
import { cn } from "../lib/utils";

interface CarouselProps {
  children: ReactNode[];
  autoPlay?: boolean;
  interval?: number;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
}

export function Carousel({
  children,
  autoPlay = false,
  interval = 3000,
  showDots = true,
  showArrows = true,
  className,
}: CarouselProps) {
  const [current, setCurrent] = useState(0);
  const count = children.length;

  useEffect(() => {
    if (!autoPlay || count <= 1) return;
    const id = setInterval(() => setCurrent(c => (c + 1) % count), interval);
    return () => clearInterval(id);
  }, [autoPlay, interval, count]);

  const prev = () => setCurrent(c => (c - 1 + count) % count);
  const next = () => setCurrent(c => (c + 1) % count);

  return (
    <div className={cn("carousel", className)}>
      <div className="carousel-viewport">
        <div className="carousel-track" style={{ transform: `translateX(-${current * 100}%)` }}>
          {children.map((child, i) => (
            <div key={i} className="carousel-slide">{child}</div>
          ))}
        </div>
      </div>

      {showArrows && count > 1 && (
        <>
          <button className="carousel-arrow carousel-arrow-prev" onClick={prev} aria-label="Previous">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button className="carousel-arrow carousel-arrow-next" onClick={next} aria-label="Next">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </>
      )}

      {showDots && count > 1 && (
        <div className="carousel-dots" role="tablist">
          {children.map((_, i) => (
            <button
              key={i}
              role="tab"
              aria-selected={i === current}
              className={cn("carousel-dot", i === current && "carousel-dot-active")}
              onClick={() => setCurrent(i)}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
